// Trustline validation service
import { trustline, TrustlineInitOptions, TrustlineWeb3ValidateParams, TrustlineValidateResponse } from '@trustline.id/websdk';

class TrustlineService {
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private initialize() {
    try {
      const initOptions: TrustlineInitOptions = {
        clientId: process.env.REACT_APP_TRUSTLINE_CLIENT_ID || 'YOUR_TRUSTLINE_CLIENT_ID',
        loginUri: process.env.REACT_APP_TRUSTLINE_LOGIN_URI || 'https://yourapp.com/auth/trustline/callback'
      };
      trustline.init(initOptions);
      this.isInitialized = true;
      console.log('Trustline SDK initialized');
    } catch (error) {
      console.error('Failed to initialize Trustline SDK:', error);
    }
  }

  async validateTransaction(data: TrustlineWeb3ValidateParams): Promise<TrustlineValidateResponse> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }

    try {
      const response = await trustline.validate(data);
      return response;
    } catch (error) {
      console.error('Trustline validation failed:', error);
      throw error;
    }
  }

  async validateCreateProposal(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    targets: string[],
    values: string[],
    calldatas: string[],
    description: string
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: '0',
        data: {
          functionSelector: 'propose(address[],uint256[],bytes[],string)',
          args: [targets, values, calldatas, description]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Create proposal transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Create proposal transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Create proposal transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateVote(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    proposalId: string,
    support: number,
    reason?: string
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: '0',
        data: {
          functionSelector: reason ? 'castVoteWithReason(uint256,uint8,string)' : 'castVote(uint256,uint8)',
          args: reason ? [proposalId, support.toString(), reason] : [proposalId, support.toString()]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Vote transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Vote transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Vote transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }
}

export const trustlineService = new TrustlineService();
